import { memo } from 'react';
import { motion } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface LiveUser {
    id: string | number;
    avatar?: string;
    photo_url?: string;
    username: string;
}

interface LiveUsersProps {
    users?: LiveUser[];
    totalActive?: number;
    onClick?: () => void;
}

export const LiveUsers = memo(function LiveUsers({
    users = [],
    totalActive = 0,
    onClick
}: LiveUsersProps) {
    const { impactOccurred } = useHapticFeedback();

    // Dynamic real data only
    const displayUsers = users.slice(0, 4).map(u => ({
        ...u,
        avatar: u.photo_url || '/icons/default-avatar.svg'
    }));

    const activeCount = totalActive > 0 ? totalActive : 0;
    const overflowCount = Math.max(0, activeCount - 4);

    return (
        <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => {
                impactOccurred('light');
                onClick?.();
            }}
            className="glass flex items-center gap-3 p-1.5 pr-4 rounded-2xl active:scale-95 transition-all group shrink-0 h-14"
        >
            <div className="flex -space-x-3 items-center ml-1">
                {displayUsers.length > 0 ? (
                    displayUsers.map((user, idx) => (
                        <motion.div
                            key={user.id}
                            initial={{ x: -10, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: idx * 0.1 }}
                            className="relative"
                            style={{ zIndex: displayUsers.length - idx }}
                        >
                            <div className="w-8 h-8 rounded-full border-2 border-[#0A0E17] bg-tg-secondary overflow-hidden shadow-lg group-hover:border-white/20 transition-colors">
                                <img
                                    src={user.avatar}
                                    alt={user.username}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {idx === 0 && (
                                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#0A0E17] animate-pulse" />
                            )}
                        </motion.div>
                    ))
                ) : (
                    <div className="glass-chip w-8 h-8 rounded-full border-2 border-[#0A0E17] flex items-center justify-center relative" style={{ zIndex: 1 }}>
                        <span className="text-[9px] text-white/30 font-bold">0</span>
                    </div>
                )}
                {/* Overflow Placeholder */}
                {overflowCount > 0 && (
                    <div 
                        className="glass-chip w-8 h-8 rounded-full border-2 border-[#0A0E17] flex items-center justify-center text-[9px] font-bold text-white/50 relative"
                        style={{ zIndex: 0 }}
                    >
                        +{overflowCount}
                    </div>
                )}
            </div>

            {/* Status Info */}
            <div className="flex flex-col items-start text-left">
                <span className="text-[8px] font-black text-white/30 uppercase tracking-[0.2em]">Live Pulse</span>
                <div className="flex items-center gap-1 mt-0.5">
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">
                        <span className="w-1 h-1 bg-red-500 rounded-full animate-ping" />
                        <span className="text-[8px] font-bold text-red-500 uppercase tracking-tighter">Live</span>
                    </div>
                    <p className="text-[9px] text-blue-400 font-bold uppercase tracking-wider">
                        {activeCount.toLocaleString()}
                    </p>
                </div>
            </div>
        </motion.button>
    );
});
